// src/components/RoomDetailsPage.js
import React, { useState, useEffect,useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Carousel,
  Badge,
  Button,
  Spinner,
  Card,
  Image,
  Placeholder,
} from "react-bootstrap";
import { ArrowLeft, CloudMoon, Minus, Plus, Share2, Users } from "lucide-react";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { app } from "../FireBase";
import "../css/RoomDetailsPage.css";
import SearchModal from "../components/SearchModal";
import Amenities from "../components/Amenities";
import RelatedRooms from "../components/RelatedRooms";
import ProfileReminder from "../components/ProfileReminder";
import { AuthContext } from "../context/AuthContext";

const db = getFirestore(app);
const STORAGE_KEY = "bookingSearch";

function htmlToText(html) {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

export default function RoomDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: null, to: null });
  const [adults, setAdults] = useState("");
  const [children, setChildren] = useState([]);
  const [index, setIndex] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const [searchModal, setSearchModal] = useState(false);
  const [bookingCounts, setBookingCounts] = useState({}); // { [roomId]: number }
  const [fullyBooked, setFulyBooked] = useState(false);
  const [availableRooms,setAvilableRooms]=useState(null);

  // track loaded state for each image and thumbnail
  const [imgLoaded, setImgLoaded] = useState({});
  const [roomCount, setRoomCount] = useState(1); // ✅ new state
  const [showReminder, setShowReminder] = useState(false);


  // Helper: categorize children
  const categorizeChildren = (childrenAges = []) => {
    let under7 = 0,
      between8And12 = 0,
      above12 = 0;
    childrenAges.forEach((age) => {
      if (age <= 7) under7++;
      else if (age <= 12) between8And12++;
      else above12++;
    });
    return { under7, between8And12, above12 };
  };

  // Calculate booking validity & adjusted price
  const calculateBooking = () => {
    if (!room) return { valid: true, finalPrice: 0 };

    const { under7, between8And12, above12 } = categorizeChildren(children);

    // Effective adults = adults + children above 12
    const effectiveAdults = adults + above12;

    // Required rooms for adults
    const requiredAdultRooms = room.occupancy
      ? Math.ceil(effectiveAdults / room.occupancy)
      : 1;

    // Required rooms for children (under 13)
    const requiredChildRooms = room.childOccupancy
      ? Math.ceil((under7 + between8And12) / room.childOccupancy)
      : 1;

    const requiredRooms = Math.max(requiredAdultRooms, requiredChildRooms);

    // 🔥 Always sync roomCount with requiredRooms
    if (roomCount !== requiredRooms) {
      setRoomCount(requiredRooms);
    }

    // Capacity validation
    const valid =
      effectiveAdults <= room.occupancy * requiredRooms &&
      under7 + between8And12 <= room.childOccupancy * requiredRooms;


    const roomAvailability = availableRooms >= requiredRooms;
    console.log(roomAvailability)
      // console.log(roomAvilablity,availableRooms,requiredRooms)
    // Base price
    let finalPrice = room.pricePerNight * requiredRooms;

    // ✅ Charge children 8–12 only if adult slots are fully filled
    const adultSlotsUsed = effectiveAdults % room.occupancy === 0;
    if (adultSlotsUsed) {
      finalPrice += between8And12 * (room.pricePerNight / room.occupancy);
    }

    return { valid, finalPrice,roomAvailability };
  };

  const { valid, finalPrice ,roomAvailability} = calculateBooking();

  const handleBookNow = () => {
    // 1. Read search data (dates + guest details) from localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    let searchData = {};
    if (saved) {
      try {
        searchData = JSON.parse(saved);
      } catch {
        searchData = {};
      }
    }

    // 2. Break down children ages
    const { under7, between8And12, above12 } = categorizeChildren(children);

    // 3. Effective adults (adults + children above 12)
    const effectiveAdults = adults + above12;

    // 4. Total guest count
    const totalGuests = adults + children.length;

    // 5. Build payload with all required data
    const bookingData = {
      roomId: room.id,
      roomName: room.name,
      roomCount,
      checkIn: searchData.from,
      checkOut: searchData.to,

      // guest counts
      adults,
      children: children.length,
      childrenAges: children,
      breakdown: { under7, between8And12, above12 },
      effectiveAdults,
      totalGuests,

      // pricing
      pricePerNight: room.pricePerNight,
      finalPrice, // ✅ from calculateBooking()
    };

    console.log("Booking payload:", bookingData);

    // 6. Navigate to checkout with full data
    navigate(`/checkout/${room.id}`, { state: bookingData });
  };

  useEffect(() => {
    if (!range.from || !range.to) {
      setBookingCounts({});
      return;
    }
    (async () => {
      const checkinDate = formatDateForOrder(range.from);
      const checkoutDate = formatDateForOrder(range.to);
      console.log("checkinDate", checkinDate);
      const bq = query(
        collection(db, "room_bookings"),
        where("checkIn", "<", checkoutDate),
        where("checkOut", ">", checkinDate),
        where("status", "==", "booking-confirmed")
      );
      const snap = await getDocs(bq);
      const counts = {};
      snap.docs.forEach((doc) => {
        const { roomId,roomCount=1 } = doc.data();
        counts[roomId] = (counts[roomId] || 0) + roomCount;
      });
      console.group("count", counts);
      setBookingCounts(counts);
    })();
  }, [range]);

  // show sticky bar after scrolling past sentinel
  useEffect(() => {
    const sentinel = document.getElementById("scroll-sentinel");
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // hydrate search data when modal closes
  useEffect(() => {
    if (!searchModal) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;

      try {
        const { from, to, adults: a, children: c } = JSON.parse(saved);

        // ✅ restore date range
        setRange({
          from: from ? new Date(from) : undefined,
          to: to ? new Date(to) : undefined,
        });

        // ✅ restore adults
        if (typeof a === "number" && a >= 1) {
          setAdults(a);
          console.log("adult", a);
        }

        // ✅ restore children (as array of ages)
        if (Array.isArray(c)) {
          console.log("child count", c.length, "ages:", c);
          setChildren(c); // already an array of ages
        }
      } catch (err) {
        console.error("Failed to parse saved search data:", err);
      }
    }
  }, [searchModal]);

  // fetch the room doc
  useEffect(() => {
    (async () => {
      const ref = doc(db, "rooms", id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const fetchedRoom = { id: snap.id, ...snap.data() };
        setRoom(fetchedRoom);

        const booked = bookingCounts[fetchedRoom.id] || 0;
        const totalRooms = fetchedRoom?.roomCount;
         setAvilableRooms (totalRooms-booked)
        if (booked >= totalRooms) {
          setFulyBooked(true);
        }
      }
      setLoading(false);
    })();
  }, [id, bookingCounts]);

  const formatDateForOrder = (date) => {
    return date?.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // page-level loading skeleton
  
  if (loading) {
    return (
      <Container fluid className="p-0"  style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: "#fff",
        
        transition: "opacity 0.3s ease",
      }}>
        <Container className="px-3 py-5">
          <Row className="gy-4">
            {/* LEFT: Carousel Skeleton */}
            <Col lg={7}>
              <div className="room-carousel-wrapper">
                {/* Main image skeleton */}
                <div
                  style={{
                    width: "100%",
                    height: 250,
                    borderRadius: "15px",
                    backgroundColor: "#e9ecef",
                    
                  }}
                ></div>
  
                {/* Thumbnail skeletons */}
                <div className="d-flex gap-2 mt-3">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: 90,
                        height: 70,
                        borderRadius: "8px",
                        backgroundColor: "#e9ecef",
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </Col>
  
            {/* RIGHT: Info Card Skeleton */}
            <Col lg={5}>
              <Card className="p-4 shadow-sm border-0">
                <div
                  style={{
                    width: "100%",
                    height: 50,
                    borderRadius: "8px",
                    backgroundColor: "#e9ecef",
                    marginBottom: "15px",
                  }}
                ></div>
                <div
                  style={{
                    width: "100%",
                    height: 180,
                    borderRadius: "8px",
                    backgroundColor: "#e9ecef",
                    marginBottom: "15px",
                  }}
                ></div>
  
                {/* Vehicle grid skeleton */}
        
  
                <div
                  style={{
                    width: "100%",
                    height: 45,
                    borderRadius: "8px",
                    backgroundColor: "#e9ecef",
                    marginTop: "20px",
                  }}
                ></div>
              </Card>
            </Col>
          </Row>
  
          {/* Related Rentals Section */}
          <div className="mt-5">
            <Row className="g-3">
              {[...Array(3)].map((_, i) => (
                <Col key={i} md={4}>
                  <div
                    style={{
                      width: "100%",
                      height: 220,
                      borderRadius: "12px",
                      backgroundColor: "#e9ecef",
                    }}
                  ></div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </Container>
    );
  }

  if (!room) {
    return (
      <Container className="py-5 text-center">
        <h4>Room not found</h4>
        <Button variant="link" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </Button>
      </Container>
    );
  }

  // build images array: main + gallery
  const images = [
    room.roomImage,
    ...(Array.isArray(room.galleryImages) && room.galleryImages.length
      ? room.galleryImages
      : []),
  ];

  return (
    <Container fluid className="p-0">
      {/* Back */}
      {/* <Container className="py-3">
        <Button variant="link" onClick={() => navigate(-1)}>
          <ArrowLeft /> Back to listing
        </Button>
      </Container> */}

      <Container className="px-3">
        <Row className="gy-4">
          {/* Left: Carousel + Thumbs */}
          <Col lg={7}>
            <div className="room-carousel-wrapper">
              <Carousel
                activeIndex={index}
                onSelect={(i) => setIndex(i)}
                variant="dark"
                className="mb-3 room-main-images"
              >
                {images.map((url, i) => (
                  <Carousel.Item key={i}>
                    {/* image loader */}
                    {!imgLoaded[`main-${i}`] && (
                      <Placeholder
                        animation="glow"
                        style={{
                          width: "100%",
                          height: 400,
                          borderRadius: ".75rem",
                          backgroundColor: "#e9ecef",
                        }}
                      />
                    )}
                    <img
                      src={url}
                      className="d-block w-100 room-main-image"
                      alt={`Slide ${i + 1}`}
                      onLoad={() =>
                        setImgLoaded((prev) => ({
                          ...prev,
                          [`main-${i}`]: true,
                        }))
                      }
                      style={{
                        display: imgLoaded[`main-${i}`] ? "block" : "none",
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>
            <div className="room-thumbs d-flex gap-2">
              {images.map((url, i) => (
                <div
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`thumb-container ${
                    index === i ? "thumb-active" : ""
                  }`}
                >
                  {/* thumbnail loader */}
                  {!imgLoaded[`thumb-${i}`] && (
                    <Placeholder
                      animation="glow"
                      style={{
                        width: 90,
                        height: 70,
                        borderRadius: ".5rem",
                        backgroundColor: "#e9ecef",
                      }}
                    />
                  )}
                  <Image
                    src={url}
                    thumbnail
                    onLoad={() =>
                      setImgLoaded((prev) => ({
                        ...prev,
                        [`thumb-${i}`]: true,
                      }))
                    }
                    style={{
                      objectFit: "cover",
                      display: imgLoaded[`thumb-${i}`] ? "block" : "none",
                    }}
                  />
                </div>
              ))}
            </div>
          </Col>

          {/* Right: Details */}
          <Col lg={5}>
            <Card className="room-details-card p-4 shadow-sm">
              <Card.Body>
                <div className="justify-content-between align-items-start mb-3">
                   {fullyBooked&&
                    <Badge
                    bg="danger"
                    pill
                  >
                    Fully booked
                  </Badge>
                  }
                  {!fullyBooked && availableRooms &&
                  <Badge
                    bg="warning"
                    pill
                  >
                    Only {availableRooms} left
                  </Badge>
                  }
                  <Card.Title className="mb-0">{room.name}</Card.Title>
                  {/* <Button variant="outline-secondary" size="sm">
                    <Share2 size={16} />
                  </Button> */}
                 
                </div>

                <h5
                  className="mb-3"
                  style={{ fontWeight: "400", color: "#54b435" }}
                >
                  Sleeps {room.occupancy} Adult{room.occupancy > 1 ? "s" : ""}
                  {room.childOccupancy > 0 && (
                    <>
                      {" "}
                      and {room.childOccupancy} Child
                      {room.childOccupancy > 1 ? "ren" : ""}
                    </>
                  )}
                </h5>

                <div className="mb-4">
                  <h4 style={{ color: "#54b435" }}>
                    ₹{finalPrice.toLocaleString()}{" "}
                    <small className="text-muted">
                      / <CloudMoon /> night ({roomCount} room
                      {roomCount > 1 ? "s" : ""})
                    </small>
                  </h4>
                  {!valid  && (
                    <div className="text-danger small mt-1">
                      This room cannot accommodate your group. Please choose
                      more rooms or another room type.
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <p>{htmlToText(room.shortDescription || "")}</p>
                </div>

                <div id="scroll-sentinel" style={{ height: 1 }} />

                <Card
                  className="stay-info mb-4"
                  onClick={() => setSearchModal(true)}
                  style={{ cursor: "pointer" }}
                >
                  <strong style={{ color: "#54b435" }}>Your stay</strong>
                  <div className="mt-2">
                    <strong>Dates:</strong>{" "}
                    {range.from && range.to ? (
                      `${range.from.toLocaleDateString()} → ${range.to.toLocaleDateString()}`
                    ) : (
                      <span className="text-muted">Not selected</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <strong>Adults:</strong>{" "}
                    {adults ? (
                      adults
                    ) : (
                      <span className="text-muted">Not selected</span>
                    )}
                    <br />
                    <strong>Children:</strong>{" "}
                    {children.length > 0 ? (
                      children.length
                    ) : (
                      <span className="text-muted">Not selected</span>
                    )}
                  </div>
                </Card>
                {/* <Card
                  className="mb-4 stay-info"
                  style={{ borderRadius: "15px" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <strong style={{ color: "#54b435" }}>Rooms:</strong>
                    <div className="d-flex align-items-center ">
                      <Button
                        size="sm"
                        onClick={() =>
                          setRoomCount((prev) => Math.max(1, prev - 1))
                        }
                        style={{
                          minWidth: 40,
                          backgroundColor: "#54b435",
                          borderColor: "#54b435",
                          borderRadius: "12px",
                          color: "white",
                        }}
                      >
                        <Minus size={20} />
                      </Button>
                      <span
                        style={{
                          minWidth: 30,
                          textAlign: "center",
                          fontWeight: "500",
                        }}
                      >
                        {roomCount}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => setRoomCount((prev) => prev + 1)}
                        style={{
                          minWidth: 40,
                          backgroundColor: "#54b435",
                          borderColor: "#54b435",
                          borderRadius: "12px",
                          color: "white",
                        }}
                      >
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                </Card> */}
               
                 {!roomAvailability && (
                  <p className="text-danger text-center">
                    Only {availableRooms} room(s) available
                  </p>
                )}
                <Button
                  size="lg"
                  className={`w-100 mb-3 ${
                    fullyBooked || !valid ? "btn-own-danger" : "btn-own"
                  }`}
                  disabled={!range.from || !range.to || fullyBooked || !valid || !roomAvailability} 
                   onClick={() => {
                    if (!currentUser) {
                      // not logged in → show reminder instead of booking
                      setShowReminder(true);
                      
                    } else {
                      handleBookNow();
                    }
                  }}
                >
                  {fullyBooked 
                    ? "Fully Booked"
                    : !valid 
                    ? "Invalid Selection"
                    : "Book Now"}
                   
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Amenities />

        {room.description && (
          <>
            <h4>Description</h4>

            <div
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: room.description }}
            />
          </>
        )}
        <br />
        <div style={{ marginBottom: "50px" }}>
          <RelatedRooms currentRoomId={room.id} />
        </div>
      </Container>

      {/* Sticky mobile bar */}
      {/* {showSticky && !searchModal && (
        <div className="mobile-book-sticky d-lg-none" style={{borderRadius:'30px 30px 0px 0px'}}>
          <Card className="p-3 mb-2 bg-white shadow-sm stay-info-mobile" style={{borderRadius:'15px'}} onClick={() => setSearchModal(true)}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold text-success">Your stay</span>
              <Badge bg="light" text="success" className="py-1 px-2">
                <small className="fw-semibold">{guests || '–'}</small>
              </Badge>
            </div>
            <div className="small text-muted">
              <div>
                <strong>Dates:</strong>{' '}
                {range.from && range.to
                  ? `${range.from.toLocaleDateString()} → ${range.to.toLocaleDateString()}`
                  : 'Not selected'}
              </div>
              <div>
                <strong>Guests:</strong> {guests || 'Not selected'}
              </div>
            </div>
          </Card>
          <Button
            size="lg"
           
            className={`w-100 mb-3 ${fullyBooked ? 'btn-own-danger' : 'btn-own'}`}
            disabled={!range.from || !range.to || fullyBooked}
            onClick={() => navigate(`/checkout/${room.id}`)}
          >
            {fullyBooked ? 'Fully Booked' : 'Book Now'}
          </Button>
        </div>
      )} */}

      <SearchModal show={searchModal} onHide={() => setSearchModal(false)} />
        {showReminder && (
          <ProfileReminder
              show={showReminder}
              onClose={() => setShowReminder(false)}
              type="login"
              auto={false}
            />
        )}

    </Container>
  );
}
